import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { gtmNewsletterSubmit } from '../utils/gtm';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SEOHead from '../components/SEO/SEOHead';
import BreadcrumbNav from '../components/SEO/BreadcrumbNav';
import TrustStrip from '../components/Layout/TrustStrip';
import AccountPicker60s from '../components/banking/AccountPicker60s';
import FeeKiller from '../components/banking/FeeKiller';
import TFSAorRRSPHelper from '../components/banking/TFSAorRRSPHelper';

/* ── Data constants ── */

const accordionItems = [
  {
    title: '¿Cuál es el mejor banco para recién llegados a Canadá?',
    content: 'Depende de tus necesidades. Tangerine y Simplii ofrecen cuentas $0 fees permanentes. Los Big Five (RBC, TD, Scotiabank, BMO, CIBC) tienen programas newcomer con 1-3 años sin cuota. Neo Financial ofrece cashback sin fees.',
  },
  {
    title: '¿Puedo abrir una cuenta sin SIN?',
    content: 'Sí. Los bancos están obligados a abrir cuentas básicas sin SIN. Solo necesitas dos IDs válidas (pasaporte + permiso de trabajo/estudio). El SIN solo se requiere para cuentas que generan intereses (TFSA, RRSP, savings).',
  },
  {
    title: '¿Chequing vs savings: cuál es la diferencia?',
    content: 'Chequing es para transacciones diarias: débito, nómina, facturas. Savings genera intereses y es para guardar dinero a mediano/largo plazo. La mayoría de los canadienses tienen ambas.',
  },
  {
    title: '¿Cuánto cobra un banco en comisiones?',
    content: 'Big Five: $4-$17/mes (básicos) hasta $30/mes (todo incluido). Bancos digitales como Tangerine, Simplii y EQ Bank: $0/mes sin límite de transacciones.',
  },
  {
    title: '¿Qué es el seguro CDIC?',
    content: 'Protege tus depósitos hasta $100,000 por categoría (chequing, savings, TFSA, RRSP) si tu banco quiebra. Casi todos los bancos principales están cubiertos.',
  },
];

const categoryPills = [
  { id: 'cuenta-ideal', label: 'Quiz' },
  { id: 'top-picks', label: 'Top picks' },
  { id: 'comparadores', label: 'Comparadores' },
  { id: 'herramientas', label: 'Herramientas' },
  { id: 'faq', label: 'FAQ' },
];

const topPicks = [
  {
    tag: 'Mejor cuenta sin fees',
    icon: <StarOutlineIcon sx={{ fontSize: '1.2rem', color: '#F9A825' }} />,
    rationale: '$0/mes + e-Transfers ilimitados + alta tasa de ahorro',
    link: '/comparar-cuentas',
  },
  {
    tag: 'Mejor cuenta para newcomers',
    icon: <PersonOutlineIcon sx={{ fontSize: '1.2rem', color: '#2E7D32' }} />,
    rationale: 'Cuota exenta 1-3 años + acceso a tarjeta de crédito sin historial',
    link: '/comparar-cuentas',
  },
  {
    tag: 'Mejor HISA (alto interés)',
    icon: <TrendingUpIcon sx={{ fontSize: '1.2rem', color: '#1E3A5F' }} />,
    rationale: '4.00%+ en ahorros, sin monto mínimo, protegido por CDIC',
    link: '/comparar-cuentas',
  },
];

const nextSteps = [
  { label: 'Construir crédito', desc: 'Guía desde cero para newcomers', to: '/blog/construir-credito-canada-desde-cero' },
  { label: 'Mejores cuentas sin fees', desc: 'HISA para fondo de emergencia', to: '/blog/mejores-cuentas-sin-fees-recien-llegados' },
  { label: 'Tasas GIC 2026', desc: 'Inversión garantizada por CDIC', to: '/tasas-gic' },
];

/* ── Schema ── */
const buildBankingSchema = (lng) => ({
  '@context': 'https://schema.org',
  '@type': 'FinancialService',
  name: 'Comparador de cuentas bancarias en Canadá - FinMaple',
  description: 'Compara cuentas de ahorro, TFSA, RRSP y los mejores bancos de Canadá para recién llegados.',
  url: 'https://www.finmaple.com/banca',
  provider: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
  areaServed: { '@type': 'Country', name: 'CA' },
  inLanguage: lng,
  availableLanguage: ['es', 'en', 'fr'],
});

/* ── Sticky Sub-Nav ── */
const StickySubNav = () => {
  const [activeSection, setActiveSection] = useState('');
  const [isSticky, setIsSticky] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!navRef.current) return;
      const rect = navRef.current.getBoundingClientRect();
      setIsSticky(rect.top <= 0);

      for (let i = categoryPills.length - 1; i >= 0; i--) {
        const el = document.getElementById(categoryPills[i].id);
        if (el) {
          const elRect = el.getBoundingClientRect();
          if (elRect.top <= 120) {
            setActiveSection(categoryPills[i].id);
            return;
          }
        }
      }
      setActiveSection('');
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box
      ref={navRef}
      component="nav"
      role="navigation"
      aria-label="Navegación de secciones de banca"
      sx={{
        position: 'sticky', top: 0, zIndex: 20, bgcolor: '#FFFFFF',
        borderBottom: '1px solid', borderColor: isSticky ? '#EEF0F3' : 'transparent',
        boxShadow: isSticky ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      }}
    >
      <Container maxWidth="lg">
        <Box
          className="mobile-scroll-container"
          sx={{ display: 'flex', gap: 0.5, py: 1, overflowX: 'auto' }}
        >
          {categoryPills.map((pill) => (
            <Button
              key={pill.id}
              onClick={() => {
                scrollTo(pill.id);
                if (window.plausible) window.plausible('Banking Tab Click', { props: { tab: pill.id } });
              }}
              disableElevation
              disableRipple
              aria-current={activeSection === pill.id ? 'true' : undefined}
              sx={{
                borderRadius: 0, height: { xs: '44px', md: '40px' }, px: 2, fontSize: '14px',
                textTransform: 'none', flexShrink: 0, transition: 'all 0.15s ease',
                ...(activeSection === pill.id
                  ? { bgcolor: 'transparent', color: '#1E3A5F', fontWeight: 700, borderBottom: '2px solid #1E3A5F', '&:hover': { bgcolor: '#F7F9FC' } }
                  : { bgcolor: 'transparent', color: '#5F6B7A', fontWeight: 500, borderBottom: '2px solid transparent', '&:hover': { bgcolor: '#F7F9FC', color: '#1E3A5F' } }),
                '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
              }}
            >
              {pill.label}
            </Button>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

/* ── Top Picks ── */
const TopPicksSection = ({ lng }) => (
  <Box>
    <Typography
      component="h2"
      sx={{ fontSize: '13px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 3 }}
    >
      Top picks por necesidad
    </Typography>
    <Grid container spacing={3}>
      {topPicks.map((pick) => (
        <Grid item xs={12} sm={4} key={pick.tag}>
          <Box
            component={RouterLink}
            to={`/${lng}${pick.link}`}
            onClick={() => {
              if (window.plausible) window.plausible('Top Pick Click', { props: { pick: pick.tag, page: 'banking' } });
            }}
            aria-label={`${pick.tag}: ${pick.rationale}`}
            sx={{
              display: 'flex', flexDirection: 'column', height: '100%',
              textDecoration: 'none', bgcolor: '#FFFFFF',
              border: '1px solid #EEF0F3', borderRadius: '12px', p: 3,
              transition: 'all 0.18s ease',
              '&:hover': { borderColor: '#1E3A5F', boxShadow: '0 4px 16px rgba(0,0,0,0.06)', transform: 'translateY(-2px)' },
              '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
              {pick.icon}
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1E3A5F', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {pick.tag}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', color: '#1A1A1A', lineHeight: 1.6, mb: 2, flex: 1 }}>
              {pick.rationale}
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1E3A5F', display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto' }}>
              Ver opciones <ArrowForwardIcon sx={{ fontSize: '14px' }} />
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Box>
);

/* ── Retention Strip ── */
const RetentionStrip = ({ lng }) => (
  <Box
    component="section"
    aria-label="Siguiente paso recomendado"
    sx={{ bgcolor: '#F7F9FC', py: { xs: 4, md: 5 }, borderTop: '1px solid #EEF0F3' }}
  >
    <Container maxWidth="lg">
      <Typography
        sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 2.5, textAlign: 'center' }}
      >
        Siguiente paso recomendado
      </Typography>
      <Grid container spacing={2.5} justifyContent="center">
        {nextSteps.map((step) => (
          <Grid item xs={12} sm={4} key={step.label}>
            <Box
              component={RouterLink}
              to={`/${lng}${step.to}`}
              aria-label={`${step.label}: ${step.desc}`}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                textDecoration: 'none', bgcolor: '#FFFFFF',
                border: '1px solid #EEF0F3', borderRadius: '12px', p: 2,
                height: '100%',
                transition: 'all 0.18s ease',
                '&:hover': { borderColor: '#1E3A5F', boxShadow: '0 4px 16px rgba(30,58,95,0.1)', transform: 'translateY(-2px)' },
                '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: '14px', mb: 0.25 }}>{step.label}</Typography>
                <Typography sx={{ fontSize: '13px', color: '#5F6B7A' }}>{step.desc}</Typography>
              </Box>
              <ArrowForwardIcon sx={{ color: '#1E3A5F', fontSize: '16px', flexShrink: 0 }} />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

/* ══════════════════════════════════════════════
   PAGE COMPONENT
   ══════════════════════════════════════════════ */
const BankingPage = () => {
  const { i18n } = useTranslation();
  const { lng } = useParams();
  const [tfsaRrspPick, setTfsaRrspPick] = useState(null);

  const handleTfsaRrspRecommendation = useCallback((pick) => {
    setTfsaRrspPick(pick);
  }, []);

  return (
    <Box sx={{ bgcolor: '#FFFFFF' }}>
      <SEOHead
        seoKey="banking"
        title="Banca en Canadá para latinos"
        description="Compara cuentas de ahorro, TFSA, RRSP y los mejores bancos de Canadá para recién llegados. Encuentra la cuenta bancaria perfecta para recién llegados."
        canonical="/banca"
        schema={buildBankingSchema(i18n.language)}
        routeKey="banking"
        noIndex={i18n.language !== 'es'}
      />

      {/* ── 1. HERO (compact, product-framed) ── */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 100%)',
          borderBottom: '1px solid #EEF0F3',
          pt: { xs: 3, md: 3.5 }, pb: { xs: 2.5, md: 3 },
        }}
      >
        <Container maxWidth="lg">
          <BreadcrumbNav items={[{ label: 'Banca' }]} />
          <Typography
            variant="h3" component="h1"
            sx={{
              fontWeight: 700, color: '#0F1E3A', mt: 1.5, mb: 1,
              lineHeight: 1.15, fontSize: { xs: '24px', md: '36px' },
              letterSpacing: '-0.02em',
            }}
          >
            Banca
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '15px', md: '16px' }, color: '#5F6B7A',
              lineHeight: 1.6, maxWidth: '60ch', mb: 1,
            }}
          >
            Encuentra la mejor cuenta para tu situación en Canadá.
            Big 5 ofrecen exención de cuotas por 12-36 meses para newcomers.
            EQ Bank, Simplii y Tangerine son $0 fees permanente.
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>
            Actualizado marzo 2026 · Datos verificados · Sin costo para usuarios
          </Typography>
        </Container>
      </Box>

      {/* ── Sticky Sub-Nav ── */}
      <StickySubNav />

      {/* ── 2. QUIZ — dominant entry point ── */}
      <Box
        component="section" id="cuenta-ideal"
        sx={{ bgcolor: '#F7F9FC', py: { xs: 4, md: 5 }, scrollMarginTop: '60px' }}
      >
        <Container maxWidth="lg">
          <AccountPicker60s />
        </Container>
      </Box>

      {/* ── 3. TOP PICKS ── */}
      <Box
        component="section" id="top-picks"
        sx={{ bgcolor: '#FFFFFF', py: { xs: 4, md: 5 }, scrollMarginTop: '60px' }}
      >
        <Container maxWidth="lg">
          <TopPicksSection lng={lng} />
        </Container>
      </Box>

      {/* ── 4. COMPARATORS — primary + secondary ── */}
      <Box
        component="section" id="comparadores"
        sx={{ bgcolor: '#F7F9FC', py: { xs: 4, md: 5 }, scrollMarginTop: '60px' }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {/* Primary: Account comparator */}
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  bgcolor: '#FFFFFF', border: '1.5px solid #D0D5DD', borderRadius: '16px',
                  p: { xs: 3, md: 4 }, height: '100%', display: 'flex', flexDirection: 'column',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                  transition: 'box-shadow 0.18s ease',
                  '&:hover': { boxShadow: '0 8px 28px rgba(0,0,0,0.07)' },
                }}
              >
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#2E7D32', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                  Comparador principal
                </Typography>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: '#1A1A1A', mb: 1, fontSize: { xs: '18px', md: '22px' } }}>
                  Comparar cuentas bancarias
                </Typography>
                <Typography sx={{ color: '#5F6B7A', fontSize: '14px', lineHeight: 1.6, mb: 3, flex: 1 }}>
                  12 cuentas analizadas con FinMaple Score, programas newcomer y bonos de bienvenida hasta $800.
                </Typography>
                <Button
                  variant="contained" component={RouterLink} to={`/${lng}/comparar-cuentas`}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                  onClick={() => {
                    if (window.plausible) window.plausible('Comparator Click', { props: { type: 'accounts', page: 'banking' } });
                  }}
                  sx={{
                    alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700,
                    fontSize: '15px', px: 3, py: 1.2, borderRadius: '10px',
                    bgcolor: '#1E3A5F', '&:hover': { bgcolor: '#1A3152' },
                  }}
                >
                  Ver comparador de cuentas
                </Button>
              </Box>
            </Grid>
            {/* Secondary: GIC rates */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  bgcolor: '#FFFFFF', border: '1px solid #EEF0F3', borderRadius: '14px',
                  p: { xs: 3, md: 3.5 }, height: '100%', display: 'flex', flexDirection: 'column',
                  transition: 'box-shadow 0.18s ease',
                  '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.06)' },
                }}
              >
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                  También disponible
                </Typography>
                <Typography variant="body1" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
                  Tasas de GIC 2026
                </Typography>
                <Typography sx={{ color: '#5F6B7A', fontSize: '14px', lineHeight: 1.6, mb: 3, flex: 1 }}>
                  Inversión garantizada protegida por CDIC hasta $100,000.
                </Typography>
                <Button
                  variant="outlined" component={RouterLink} to={`/${lng}/tasas-gic`}
                  onClick={() => {
                    if (window.plausible) window.plausible('Comparator Click', { props: { type: 'gic', page: 'banking' } });
                  }}
                  sx={{
                    alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600,
                    fontSize: '13px', borderRadius: '8px', color: '#1E3A5F', borderColor: '#D0D5DD',
                    '&:hover': { borderColor: '#1E3A5F', bgcolor: '#F7F9FC' },
                  }}
                >
                  Ver tasas GIC
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── 5. FEE CALCULATOR (primary tool) ── */}
      <Box
        component="section" id="herramientas"
        sx={{ bgcolor: '#FFFFFF', py: { xs: 4, md: 5 }, scrollMarginTop: '60px' }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4" component="h2"
            sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: { xs: '20px', md: '24px' }, mb: 3 }}
          >
            Calculadora de comisiones bancarias
          </Typography>
          <FeeKiller />
        </Container>
      </Box>

      {/* ── 6. TFSA vs RRSP (secondary planning, lighter bg) ── */}
      <Box component="section" sx={{ bgcolor: '#F7F9FC', py: { xs: 4, md: 5 }, borderTop: '1px solid #EEF0F3' }}>
        <Container maxWidth="lg">
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
            Planificación
          </Typography>
          <Typography
            variant="h4" component="h2"
            sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: { xs: '20px', md: '24px' }, mb: 1 }}
          >
            ¿TFSA o RRSP?
          </Typography>
          <Typography sx={{ color: '#5F6B7A', fontSize: '14px', mb: 3 }}>
            Responde 3 preguntas y descubre cuál conviene más en tu situación.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <TFSAorRRSPHelper onRecommendation={handleTfsaRrspRecommendation} />
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2.5}>
                {/* TFSA card */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      bgcolor: '#FFFFFF', border: '2px solid',
                      borderColor: tfsaRrspPick === 'TFSA' ? '#1E3A5F' : '#EEF0F3',
                      borderRadius: '14px',
                      boxShadow: tfsaRrspPick === 'TFSA' ? '0 4px 20px rgba(30,58,95,0.15)' : 'none',
                      transition: 'all 0.3s ease',
                      p: 3, height: '100%',
                      display: 'flex', flexDirection: 'column', position: 'relative',
                    }}
                  >
                    {tfsaRrspPick === 'TFSA' && (
                      <Box sx={{ position: 'absolute', top: -1, right: 16, bgcolor: '#1E3A5F', color: '#fff', fontSize: '11px', fontWeight: 700, px: 1.5, py: 0.25, borderRadius: '0 0 6px 6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Recomendada
                      </Box>
                    )}
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                      TFSA
                    </Typography>
                    <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '15px', mb: 1 }}>
                      Cuenta de Ahorro Libre de Impuestos
                    </Typography>
                    <Typography sx={{ color: '#5F6B7A', fontSize: '14px', lineHeight: 1.6, mb: 2.5, flex: 1 }}>
                      Ahorra e invierte sin pagar impuestos sobre las ganancias. Retira cuando quieras.
                    </Typography>
                    <Button
                      variant="outlined" size="small" component={RouterLink} to={`/${lng}/financlopedia/tfsa-canada`}
                      sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, fontSize: '12px', borderRadius: '8px', color: '#1E3A5F', borderColor: '#D0D5DD' }}
                    >
                      Guía TFSA
                    </Button>
                  </Box>
                </Grid>
                {/* RRSP card */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      bgcolor: '#FFFFFF', border: '2px solid',
                      borderColor: tfsaRrspPick === 'RRSP' ? '#7D5A00' : '#EEF0F3',
                      borderRadius: '14px',
                      boxShadow: tfsaRrspPick === 'RRSP' ? '0 4px 20px rgba(125,90,0,0.15)' : 'none',
                      transition: 'all 0.3s ease',
                      p: 3, height: '100%',
                      display: 'flex', flexDirection: 'column', position: 'relative',
                    }}
                  >
                    {tfsaRrspPick === 'RRSP' && (
                      <Box sx={{ position: 'absolute', top: -1, right: 16, bgcolor: '#7D5A00', color: '#fff', fontSize: '11px', fontWeight: 700, px: 1.5, py: 0.25, borderRadius: '0 0 6px 6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Recomendada
                      </Box>
                    )}
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                      RRSP
                    </Typography>
                    <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '15px', mb: 1 }}>
                      Plan Registrado de Ahorro para el Retiro
                    </Typography>
                    <Typography sx={{ color: '#5F6B7A', fontSize: '14px', lineHeight: 1.6, mb: 2.5, flex: 1 }}>
                      Reduce tu ingreso gravable hoy. Perfecto si buscas beneficio fiscal inmediato.
                    </Typography>
                    <Button
                      variant="outlined" size="small" component={RouterLink} to={`/${lng}/financlopedia/rrsp-canada`}
                      sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, fontSize: '12px', borderRadius: '8px', color: '#1E3A5F', borderColor: '#D0D5DD' }}
                    >
                      Guía RRSP
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── 7. RELATED GUIDES (compact text links) ── */}
      <Box component="section" sx={{ bgcolor: '#FFFFFF', py: { xs: 2.5, md: 3 } }}>
        <Container maxWidth="lg">
          <Typography sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '14px', mb: 1.5 }}>
            Guías relacionadas
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button
              component={RouterLink} to={`/${lng}/blog/construir-credito-canada-desde-cero`} size="small"
              onClick={() => { if (window.plausible) window.plausible('Cross Link Click', { props: { from: 'banking', to: 'construir-credito' } }); }}
              sx={{ textTransform: 'none', color: '#1E3A5F', fontSize: '13px', fontWeight: 600, borderRadius: '8px', border: '1px solid #EEF0F3', px: 2, '&:hover': { bgcolor: '#F7F9FC', borderColor: '#1E3A5F' } }}
            >
              Cómo construir crédito en Canadá
            </Button>
            <Button
              component={RouterLink} to={`/${lng}/blog/mejores-cuentas-sin-fees-recien-llegados`} size="small"
              onClick={() => { if (window.plausible) window.plausible('Cross Link Click', { props: { from: 'banking', to: 'cuentas-sin-fees' } }); }}
              sx={{ textTransform: 'none', color: '#1E3A5F', fontSize: '13px', fontWeight: 600, borderRadius: '8px', border: '1px solid #EEF0F3', px: 2, '&:hover': { bgcolor: '#F7F9FC', borderColor: '#1E3A5F' } }}
            >
              Mejores cuentas sin fees para newcomers
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── 8. FAQ ── */}
      <Box component="section" id="faq" sx={{ bgcolor: '#F7F9FC', py: { xs: 4, md: 5 }, scrollMarginTop: '60px' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: { xs: '20px', md: '24px' }, mb: 2 }}>
            Preguntas frecuentes
          </Typography>
          {accordionItems.map((item, index) => (
            <Accordion
              key={index}
              disableGutters
              sx={{
                border: '1px solid #EEF0F3', boxShadow: 'none', '&:before': { display: 'none' },
                '&:not(:last-child)': { borderBottom: 'none' },
                ...(index === 0 && { borderRadius: '12px 12px 0 0 !important' }),
                ...(index === accordionItems.length - 1 && { borderRadius: '0 0 12px 12px !important' }),
                ...(index > 0 && index < accordionItems.length - 1 && { borderRadius: '0 !important' }),
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                  {item.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.7 }}>
                  {item.content}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* ── 9. RETENTION (next steps) ── */}
      <RetentionStrip lng={lng} />

      {/* ── 10. TRUST STRIP ── */}
      <TrustStrip />

      {/* ── 11. COMPACT NEWSLETTER (secondary, not dominant) ── */}
      <Box component="section" sx={{ bgcolor: '#FFFFFF', borderTop: '1px solid #EEF0F3', py: { xs: 2.5, md: 3 } }}>
        <Container maxWidth="lg">
          <Box
            component="form"
            action="https://buttondown.com/api/emails/embed-subscribe/Finmaple"
            method="post"
            onSubmit={() => {
              if (window.plausible) window.plausible('Newsletter Submit', { props: { source: 'banca-compact' } });
              gtmNewsletterSubmit({ source: 'banca-compact' });
            }}
            sx={{
              display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center', gap: { xs: 1.5, sm: 2 },
              maxWidth: 580, mx: 'auto',
            }}
          >
            <EmailOutlinedIcon sx={{ fontSize: 18, color: '#1E3A5F', display: { xs: 'none', sm: 'block' } }} />
            <Typography sx={{ fontSize: '13px', color: '#5F6B7A', fontWeight: 500 }}>
              Recibe comparaciones y guías financieras cada semana.
            </Typography>
            <TextField
              name="email" type="email" required size="small" placeholder="tu@email.com"
              inputProps={{ inputMode: 'email', autoComplete: 'email' }}
              sx={{
                flex: 1, minWidth: { xs: 0, sm: 160 },
                '& .MuiOutlinedInput-root': {
                  height: 44, borderRadius: '10px', fontSize: '14px',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#1E3A5F' },
                  '&.Mui-focused fieldset': { borderColor: '#1E3A5F' },
                },
              }}
            />
            <Button
              type="submit" variant="contained" size="small"
              sx={{
                height: 44, px: 2.5, fontSize: '14px', fontWeight: 700,
                borderRadius: '10px', bgcolor: '#1E3A5F', color: '#fff',
                textTransform: 'none', whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#1A3152' },
              }}
            >
              Suscribirme
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default BankingPage;
